interface AudioResponse {
  isFinal: boolean;
  transcript: string;
  error?: string;
}

class SpeechToText {
  private ws: WebSocket | null;
  private isConnected: boolean;
  private reconnectAttempts: number;
  private maxReconnectAttempts: number;
  private baseReconnectDelay: number;
  private currentAccessToken: string;
  private currentHandler:
    | ((transcriptData: { final: boolean; transcript: string }) => void)
    | null;
  private reconnectTimeout: NodeJS.Timeout | null;

  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.baseReconnectDelay = 1000; // Start with 1 second delay
    this.currentAccessToken = "";
    this.currentHandler = null;
    this.reconnectTimeout = null;
  }

  private async attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      return;
    }

    // Exponential backoff delay
    const delay = this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts);

    this.reconnectTimeout = setTimeout(async () => {
      console.info(
        `Attempting to reconnect... (Attempt ${this.reconnectAttempts + 1})`
      );
      this.reconnectAttempts++;

      if (this.currentHandler && this.currentAccessToken) {
        await this.startRecognition(
          this.currentAccessToken,
          this.currentHandler
        );
      }
    }, delay);
  }

  /**
   * Starts a new WebSocket connection for speech recognition
   */
  public async startRecognition(
    accessToken: string,
    handleTranscription: (transcriptData: {
      final: boolean;
      transcript: string;
    }) => void
  ) {
    this.currentAccessToken = accessToken;
    this.currentHandler = handleTranscription;

    if (!this.isConnected) {
      try {
        const wsUrl = `wss://websocket-server-speech-to-text-1016147129694.us-central1.run.app?token=${accessToken}`;
        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = "arraybuffer"; // Ensure binary data is properly handled

        this.ws.onopen = () => {
          console.info("WebSocket connection established");
          this.isConnected = true;
          this.reconnectAttempts = 0; // Reset reconnect attempts
        };

        this.ws.onmessage = (event) => {
          try {
            const response: AudioResponse = JSON.parse(event.data);

            if (response.error) {
              console.error("Server error:", response.error);
              this.endRecognition();
              return;
            }

            handleTranscription({
              final: response.isFinal,
              transcript: response.transcript,
            });
          } catch (error) {
            console.error("Error processing WebSocket message:", error);
          }
        };

        this.ws.onerror = (error) => {
          console.error("WebSocket error:", error);
          this.handleDisconnection();
        };

        this.ws.onclose = (event) => {
          console.warn("WebSocket closed:", event.code, event.reason);
          this.handleDisconnection();
        };
      } catch (error) {
        console.error("Error starting WebSocket connection:", error);
        this.handleDisconnection();
      }
    }
  }

  private handleDisconnection() {
    this.isConnected = false;
    this.ws = null;

    // Attempt reconnect only if max attempts are not reached
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.attemptReconnect();
    }
  }

  /**
   * Writes audio data to WebSocket stream
   */
  public writeToStream(audioData: Int16Array) {
    if (this.ws && this.isConnected) {
      try {
        // Send the raw Int16Array buffer directly
        this.ws.send(audioData.buffer);
      } catch (error) {
        console.error("Error writing to WebSocket:", error);
      }
    }
  }

  /**
   * Ends the WebSocket connection and cancels reconnection attempts
   */
  public endRecognition() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      this.currentAccessToken = "";
      this.currentHandler = null;
      this.reconnectAttempts = 0;
      console.info("Recognition connection ended");
    }
  }
}

export default SpeechToText;
